import dayjs from "dayjs";

/**
 * Turns the open-house form rows into the shape addListing / updateListing
 * expect.
 *
 * The `_id` round-trip is the load-bearing part. The server matches an
 * incoming slot to the stored one by id and carries the assigned host across
 * (mergeOpenHouses, fyrstup-server/src/utils/openHouses.js). Omit it and every
 * listing edit silently drops the host a broker assigned.
 *
 * `host` is deliberately never sent — assignOpenHouseHost is its only writer.
 */
export const formatOpenHousesForSave = (openHouses) =>
  (openHouses ?? [])
    .filter((oh) => oh?.dateDayJS && oh?.startTimeDayJS && oh?.endTimeDayJS)
    .map(({ _id, dateDayJS, startTimeDayJS, endTimeDayJS, needHost }) => {
      const at = (time) =>
        dateDayJS
          .hour(time.hour())
          .minute(time.minute())
          .second(0)
          .millisecond(0)
          .toISOString();

      return {
        // Only present on a slot that came back from the server; a row the
        // broker just added has none, which is how the server tells them apart.
        ...(_id ? { _id } : {}),
        needHost: needHost || false,
        startTime: at(startTimeDayJS),
        endTime: at(endTimeDayJS),
      };
    });

/**
 * The other half of the round-trip: turns the stored slots into the rows the
 * open-house pickers bind to.
 *
 * Dropping `_id` here is just as damaging as dropping it on save — the server
 * would see every slot as new and the assigned host would not survive the
 * edit.
 */
export const hydrateOpenHouseRows = (openHouses) =>
  (openHouses ?? []).map((oh) => {
    const start = dayjs(oh.startTime);
    const end = dayjs(oh.endTime);

    return {
      // Absent on a legacy slot that was stored without one; left undefined
      // rather than invented, so the save treats it as new.
      ...(oh._id ? { _id: oh._id } : {}),
      startTime: oh.startTime,
      endTime: oh.endTime,
      startTimeDayJS: start,
      endTimeDayJS: end,
      startTimeFormatted: start.format("h:mm A"),
      endTimeFormatted: end.format("h:mm A"),
      date: start.format("YYYY-MM-DD"),
      dateDayJS: start.startOf("day"),
      needHost: oh.needHost,
    };
  });
